import React from "react";

import {
  formatAdIIProfileStatusLabel,
  getAdIIKycLinkScenario,
  getAdIIKycShareLink,
  getAdIIProfileStatus,
} from "../../../../utils/remitterKycFlow";
import { getKycLinkCopy } from "./copy";
import type { AdIIKycLinkPanelProps } from "./copy";
import { RecipientsBlock } from "./parts";
import {
  ApprovedActions,
  GenerateNewActions,
  InProgressActions,
  LinkSentActions,
  NotSentActions,
} from "./scenarios";
import { kycLinkStyles as styles } from "./styles";

export const AdIIKycLinkPanel: React.FC<AdIIKycLinkPanelProps> = ({
  phone,
  email,
  profileDetails,
  isLinkSentLocally = false,
  loading = false,
  onSendLink,
  onResendLink,
  onGenerateNewLink,
  onCopyLink,
  onProceed,
  isRemitterDetailsIncomplete = false,
}) => {
  const scenario = getAdIIKycLinkScenario(profileDetails, isLinkSentLocally);
  const profileStatus = getAdIIProfileStatus(profileDetails);
  const statusLabel = formatAdIIProfileStatusLabel(profileDetails);
  const copyLink = getAdIIKycShareLink(profileDetails);
  const { badge, subtitle, recipientsLabel } = getKycLinkCopy({
    scenario,
    statusLabel,
    profileStatus,
  });

  const proceedLabel = isRemitterDetailsIncomplete
    ? "Proceed to Remitter Details"
    : "Proceed to Recipient";

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>Send the KYC link</h3>
          <span className={badge.className}>{badge.label}</span>
        </div>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      <RecipientsBlock label={recipientsLabel} phone={phone} email={email} />

      {scenario === "not_sent" && <NotSentActions loading={loading} onSendLink={onSendLink} />}

      {scenario === "link_sent" && (
        <LinkSentActions
          sentAt={profileDetails?.modifiedOn || profileDetails?.createdOn}
          expiresAt={profileDetails?.captureExpiresAt}
          showResend={Boolean(profileDetails?.canResend) || isLinkSentLocally}
          showCopy={(Boolean(profileDetails?.canCopyLink) || isLinkSentLocally) && Boolean(copyLink)}
          copyLink={copyLink}
          loading={loading}
          proceedLabel={proceedLabel}
          footerNote={
            isRemitterDetailsIncomplete
              ? "Next step: complete the remitter details for this remittance."
              : "Next step: add the recipient details for this remittance."
          }
          onResendLink={onResendLink}
          onCopyLink={onCopyLink}
          onProceed={onProceed}
        />
      )}

      {scenario === "in_progress" && <InProgressActions onProceed={onProceed} />}

      {scenario === "approved" && (
        <ApprovedActions proceedLabel={proceedLabel} onProceed={onProceed} />
      )}

      {(scenario === "generate_new" || scenario === "rejected") && (
        <GenerateNewActions
          title={
            scenario === "rejected"
              ? "KYC rejected"
              : profileStatus === "cancelled"
                ? "KYC cancelled"
                : "Link expired"
          }
          body={
            scenario === "rejected"
              ? "Generate a new link so the remitter can complete KYC again."
              : "This link is no longer valid. Generate a new link to continue."
          }
          loading={loading}
          disabled={loading || !profileDetails?.uniqueKey}
          onGenerateNewLink={onGenerateNewLink}
        />
      )}
    </div>
  );
};
